import React, { useEffect, useRef, useState } from "react";
import { eventRoomStore } from "@/stores/event";
import NoSignal from "../../assets/signal/no_sig.svg";
import signal3 from "../../assets/signal/3.svg";
import signal4 from "../../assets/signal/4.svg";
import signal5 from "../../assets/signal/5.svg";
import SignalCellularConnectedNoInternet0BarIcon from "@mui/icons-material/SignalCellularConnectedNoInternet0Bar";
import {
  initialSmootherState,
  nextSmootherState,
  qualityFromStats,
} from "./network-quality";
import "./network-indicator.scss";

const NetworkIndicator = () => {
  // Seed from the store so a remount (panel toggles, layout switches) shows
  // the last known level instead of blanking until the next Agora tick.
  const seed = eventRoomStore.state.networkQuality || 0;
  const smoother = useRef(initialSmootherState(seed));
  const [networkQuality, setNetworkQuality] = useState(seed);

  useEffect(() => {
    const webClient = eventRoomStore.rtcClient;
    if (!webClient?.rtc) return;

    const show = (value: number) => {
      setNetworkQuality(value);
      eventRoomStore.setNetworkQuality(value);
    };

    // Le bus RTC (subscribeClientEvents) émet [arg1, arg2] — déstructurer pour
    // récupérer l'objet stats, sinon downlink/uplink sont undefined → 0 → pas
    // d'indicateur (le signal reste bloqué sur "no_sig").
    const handleNetworkQuality = ([stats]: any) => {
      smoother.current = nextSmootherState(smoother.current, qualityFromStats(stats));
      show(smoother.current.shown);
    };

    // A real outage must not wait for UNKNOWN_TICKS: drop to no-signal as soon
    // as the SDK reports it, and let the next quality tick rebuild from there.
    const handleConnectionState = ([curState]: any) => {
      if (curState === "DISCONNECTED" || curState === "RECONNECTING") {
        smoother.current = initialSmootherState(0);
        show(0);
      }
    };

    webClient.rtc.on("network-quality", handleNetworkQuality);
    webClient.rtc.on("connection-state-change", handleConnectionState);

    return () => {
      webClient.rtc.off("network-quality", handleNetworkQuality);
      webClient.rtc.off("connection-state-change", handleConnectionState);
    };
  }, [eventRoomStore.rtcClient]);

  return (
    <div className="network-signal-container">
      {networkQuality === 0 && <img src={NoSignal} alt="Pas de signal" />}
      {(networkQuality === 1 || networkQuality === 2) && (
        <div className="network-signal-container-blink">
          <SignalCellularConnectedNoInternet0BarIcon />
        </div>
      )}
      {networkQuality === 3 && <img src={signal3} alt="Signal moyen" />}
      {networkQuality === 4 && <img src={signal4} alt="Bon signal" />}
      {networkQuality === 5 && <img src={signal5} alt="Excellent signal" />}
    </div>
  );
};

export default React.memo(NetworkIndicator);
